import type { AutocompleteRenderValueGetItemProps } from '@mui/material/Autocomplete';
import { strataOptionNumber } from './descriptionValidation';
import FindingRefChip from './FindingRefChip';
import type { StrataRouteMap } from './strataRefRoutes';

/**
 * How a stored Befundnummer reads as a chip: the (first) option that names it,
 * or the bare value for a free-typed number no option matches.
 */
export function strataChipLabel(options: readonly string[]) {
    const labelByNumber = new Map<string, string>();
    for (const option of options) {
        const number = strataOptionNumber(option).toLowerCase();
        if (!labelByNumber.has(number)) {
            labelByNumber.set(number, option);
        }
    }
    return (entry: string) =>
        labelByNumber.get(strataOptionNumber(entry).toLowerCase()) ?? entry;
}

/**
 * A `renderValue` for the positional stratigraphy relations. The stored value is
 * the bare Befundnummer, but the chip should read like the option that produced
 * it — `"Bef. 7000, Fl. 1, Pl. 1"`. Given the field's formatted options, this
 * maps each selected number back to its (first) full label, falling back to the
 * bare value for a free-typed number with no matching option.
 *
 * The chip is the same `FindingRefChip` the description pickers ("im Profil
 * mit", "identisch mit") use, so a stratigraphy reference links to the record it
 * names wherever it stands — `routes` says where (see useStrataRouteMap). A
 * number nothing resolves prints as an ordinary chip.
 */
export function renderStrataChips(
    options: readonly string[],
    routes?: StrataRouteMap,
    onOpenRef?: (to: string) => void
) {
    const labelOf = strataChipLabel(options);
    return (
        value: string[],
        getItemProps: AutocompleteRenderValueGetItemProps<true>
    ) =>
        value.map((entry, index) => {
            const { key, ...tagProps } = getItemProps({ index });
            const number = strataOptionNumber(entry).toLowerCase();
            return (
                <FindingRefChip
                    key={key}
                    {...tagProps}
                    label={labelOf(entry)}
                    to={routes?.get(number)}
                    onOpen={onOpenRef}
                />
            );
        });
}
